import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import toast from "react-hot-toast";
import { Upload, File, X } from "lucide-react";
import { useKnowledge } from "../../hooks/useKnowledge";

const UploadDocumentsTab = ({ agentData, onKnowledgeAdd }) => {
  const { uploadDocument, isUploadingDocument } = useKnowledge(agentData?.id);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    file: null,
  });
  const [uploadProgress, setUploadProgress] = useState({});

  const validateFile = (file) => {
    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "text/plain",
    ];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      return "Tipe file tidak didukung. Hanya PDF, DOCX, dan TXT yang diperbolehkan.";
    }

    if (file.size > maxSize) {
      return "Ukuran file terlalu besar. Maksimal 10MB.";
    }

    return null;
  };

  const onDrop = (acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      toast.error(error);
      return;
    }

    setFormData((prev) => ({
      ...prev,
      file: file,
      title: file.name.replace(/\.[^/.]+$/, ""), // Remove file extension
    }));

    toast.success(`File "${file.name}" siap untuk diunggah`);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "text/plain": [".txt"],
    },
    multiple: false,
    maxFiles: 1,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.file) {
      toast.error("File wajib dipilih");
      return;
    }

    try {
      // Create FormData for file upload - only files field required
      const uploadFormData = new FormData();
      uploadFormData.append("files", formData.file);

      setUploadProgress({ [formData.file.name]: 0 });

      // Upload using hook function with enhanced progress tracking
      await uploadDocument({
        formData: uploadFormData,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress({ [formData.file.name]: progress });

          // Enhanced progress messages
          if (progress < 20) {
            toast.loading("Uploading file...", { id: "upload-progress" });
          } else if (progress < 60) {
            toast.loading("Processing document...", { id: "upload-progress" });
          } else if (progress < 90) {
            toast.loading("Creating embeddings...", { id: "upload-progress" });
          } else {
            toast.loading("Finalizing...", { id: "upload-progress" });
          }
        },
      });

      // Clear progress and form
      toast.dismiss("upload-progress");
      setUploadProgress({});
      setFormData({ title: "", content: "", file: null });

      // Success handled by hook
      // Call parent callback if provided
      if (onKnowledgeAdd) {
        onKnowledgeAdd({
          title: formData.file.name.replace(/\.[^/.]+$/, ""), // Use filename without extension as title
          type: "document",
          fileName: formData.file.name,
        });
      }
    } catch (error) {
      setUploadProgress({});
      // Error handling is now managed by the hook
      console.error("Upload error:", error);
    }
  };

  const removeFile = () => {
    setFormData((prev) => ({ ...prev, file: null, title: "", content: "" }));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* File Upload Area */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Upload Dokumen *
          </label>

          {!formData.file ? (
            <div
              {...getRootProps()}
              className={`
                border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
                ${
                  isDragActive
                    ? "border-blue-400 bg-blue-50 dark:bg-blue-900/20"
                    : "border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500"
                }
              `}
            >
              <input {...getInputProps()} />
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <div className="space-y-1">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {isDragActive
                    ? "Drop file di sini..."
                    : isUploadingDocument
                    ? "Sedang memproses dokumen..."
                    : "Drag & drop file atau klik untuk memilih"}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-500">
                  {isUploadingDocument
                    ? "Mohon tunggu, proses dapat memakan waktu hingga 90 detik"
                    : "Mendukung PDF, DOCX, TXT (Max. 10MB recommended)"}
                </p>
              </div>
            </div>
          ) : (
            <div className="border border-gray-300 dark:border-gray-600 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <File className="h-8 w-8 text-blue-500" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      {formData.file.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatFileSize(formData.file.size)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={removeFile}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                >
                  <X className="h-4 w-4 text-gray-500" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Submit Button */}
        {formData.file && (
          <div className="space-y-4">
            {/* Upload Progress */}
            {Object.keys(uploadProgress).length > 0 && (
              <div className="space-y-2">
                {Object.entries(uploadProgress).map(([fileName, progress]) => (
                  <div key={fileName} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">
                        Processing {fileName}
                      </span>
                      <span className="text-gray-600 dark:text-gray-400">
                        {progress}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {progress < 20 && "Uploading file..."}
                      {progress >= 20 &&
                        progress < 60 &&
                        "Processing document..."}
                      {progress >= 60 &&
                        progress < 90 &&
                        "Creating embeddings..."}
                      {progress >= 90 && "Finalizing..."}
                    </p>
                  </div>
                ))}
              </div>
            )}

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={isUploadingDocument}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isUploadingDocument ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing Document...
                  </>
                ) : (
                  "Upload & Process Document"
                )}
              </button>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default UploadDocumentsTab;
